import { assetUrl } from '../api/client';
import { providerLabel } from '../lib/videoProvider';

/**
 * Responsive gallery grid of activity media thumbnails. Each tile opens the
 * fullscreen `MediaCarousel` at its index via `onOpen(index)`.
 *
 *   - image: thumbnail <img>
 *   - video: <video> poster frame with a play badge
 *   - embed: provider-colored tile with a play icon + provider badge
 *
 * Renders nothing when `items` is empty (caller may also guard).
 */
export default function GalleryGrid({ items = [], onOpen }) {
  if (!items.length) return null;

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
      {items.map((item, i) => (
        <li key={item.id ?? i}>
          <button
            type="button"
            onClick={() => onOpen?.(i)}
            className="group relative block aspect-square w-full overflow-hidden rounded-xl bg-maroon-50 shadow-soft ring-1 ring-cream-300 transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
            aria-label={
              item.type === 'image'
                ? item.alt_text || `View photo ${i + 1}`
                : item.type === 'embed'
                  ? `Play ${providerLabel(item.provider)} video`
                  : `Play video ${i + 1}`
            }
          >
            {item.type === 'image' && (
              <img
                src={assetUrl(item.url)}
                alt={item.alt_text || ''}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}

            {item.type === 'video' && (
              <>
                <video
                  src={assetUrl(item.url)}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full bg-black object-cover"
                />
                <PlayOverlay label="Video" />
              </>
            )}

            {item.type === 'embed' && (
              <>
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-maroon-800 to-maroon-950" />
                <PlayOverlay label={providerLabel(item.provider)} />
              </>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

function PlayOverlay({ label }) {
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/85 text-maroon-800 shadow-lift transition-transform duration-300 group-hover:scale-110">
          <svg viewBox="0 0 24 24" className="ml-0.5 h-6 w-6" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </div>
      <span className="absolute bottom-2 left-2 rounded-full bg-maroon-950/70 px-2.5 py-1 text-xs font-medium text-cream-100">
        {label}
      </span>
    </>
  );
}
